/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// document.querySelectorAll('.accordion-title').forEach(function(title){

//   title.addEventListener('click', function(){

//     const item = this.parentElement;
//     const content = item.querySelector('.accordion-content');

//     if(item.classList.contains('active')){
//       item.classList.remove('active');
//       content.style.maxHeight = null;
//     } else {
//       item.classList.add('active');
//       content.style.maxHeight = content.scrollHeight + "px";
//     }

//   });

// });
// document.querySelectorAll("#accordion > li").forEach(function(item){

//   item.addEventListener("click", function(){

//     const inner = this.nextElementSibling;

//     document.querySelectorAll("#accordion .inner").forEach(el=>{
//       if(el !== inner){
//         el.style.display = "none";
//       }
//     });

//     document.querySelectorAll("#accordion > li").forEach(el=>{
//       if(el !== this){
//         el.classList.remove("active");
//       }
//     });

//     if(inner.style.display === "block"){
//       inner.style.display = "none";
//       this.classList.remove("active");
//     } else {
//       inner.style.display = "block";
//       this.classList.add("active");
//     }

//   });

// });
document.addEventListener('DOMContentLoaded', function () {
  // Nav dropdown close delay: theme default 250ms, set to 150ms.
  // Used by MainNavigation.onNavParentHoverOut in main.js.
  const mainNav = document.getElementById('main-nav');
  if (mainNav) mainNav.navHoverDelay = 150;

  // Remove any existing tooltip
  const existing = document.querySelector('.wsl-badge-tooltip');
  if (existing) existing.remove();

  // Create tooltip with all inline styles
  const tooltip = document.createElement('div');
  tooltip.className = 'wsl-badge-tooltip';
  tooltip.style.cssText = [
    'position:fixed',
    'background:#222',
    'color:#fff',
    'font-size:12px',
    'font-weight:500',
    'padding:6px 12px',
    'border-radius:4px',
    'white-space:nowrap',
    'pointer-events:none',
    'opacity:0',
    'visibility:hidden',
    'z-index:2147483647',
    'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
    'top:0',
    'left:0',
    'transition:opacity 0.15s ease'
  ].join(';');

  document.documentElement.appendChild(tooltip);

  let mouseX = 0;
  let mouseY = 0;

  // Track mouse globally
  document.addEventListener('mousemove', function (e) {
    mouseX = e.clientX;
    mouseY = e.clientY;
    if (tooltip.style.visibility === 'visible') {
      positionTooltip();
    }
  });

  function positionTooltip() {
    const w = tooltip.offsetWidth;
    const h = tooltip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = mouseX - w / 2;
    let y = mouseY - h - 12;

    // Keep within viewport
    if (x < 8) x = 8;
    if (x + w > vw - 8) x = vw - w - 8;
    if (y < 8) y = mouseY + 20; // flip below if no space above

    tooltip.style.left = x + 'px';
    tooltip.style.top = y + 'px';
  }

  function showTooltip(text, e) {
    mouseX = e.clientX;
    mouseY = e.clientY;

    // Clear and set text
    tooltip.innerHTML = '';
    tooltip.appendChild(document.createTextNode(text));

    // Arrow
    const arrow = document.createElement('span');
    arrow.style.cssText = [
      'position:absolute',
      'top:100%',
      'left:50%',
      'transform:translateX(-50%)',
      'width:0',
      'height:0',
      'border-left:5px solid transparent',
      'border-right:5px solid transparent',
      'border-top:5px solid #222'
    ].join(';');
    tooltip.appendChild(arrow);

    // Show
    tooltip.style.visibility = 'visible';
    tooltip.style.opacity = '1';

    // Position after render
    requestAnimationFrame(positionTooltip);
  }

  function hideTooltip() {
    tooltip.style.visibility = 'hidden';
    tooltip.style.opacity = '0';
  }

  function initBadges() {
    // Find all badges inside product-block only
    const badges = document.querySelectorAll('.product-block .product-badge__badge:not([data-tooltip-init])');

    badges.forEach(function (badge) {
      // Mark as initialized
      badge.setAttribute('data-tooltip-init', 'true');

      // Fix pointer events so hover works
      badge.style.pointerEvents = 'auto';
      badge.style.cursor = 'default';
      badge.style.position = 'relative';
      badge.style.zIndex = '20';

      // Get title from span text
      const titleSpan = badge.querySelector('span:not(.product-badge__color)');
      const img = badge.querySelector('img');
      let title = '';

      if (titleSpan) {
        title = titleSpan.textContent.trim();
        // Hide the span
        titleSpan.style.cssText = 'display:none !important';
      }

      // Fallback to img alt
      if (!title && img) {
        title = img.getAttribute('alt') || '';
      }

      if (!title) return;

      badge.addEventListener('mouseenter', function (e) {
        e.stopPropagation();
        showTooltip(title, e);
      }, true); // useCapture = true to fire before link

      badge.addEventListener('mouseleave', function (e) {
        e.stopPropagation();
        hideTooltip();
      }, true);

      badge.addEventListener('mousemove', function (e) {
        e.stopPropagation();
      }, true);
    });
  }

  // Run on load
  initBadges();

  // Watch for dynamic content
  const observer = new MutationObserver(function (mutations) {
    let shouldInit = false;
    mutations.forEach(function (m) {
      if (m.addedNodes.length) shouldInit = true;
    });
    if (shouldInit) initBadges();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  console.log('WSL Badge tooltip ready. Badges found:', document.querySelectorAll('.product-block .product-badge__badge').length);
});